import { useEffect } from 'react';

/**
 * Custom hook to provide page/content protections.
 *
 * @param {Object} options Configuration options to enable/disable protections
 * @param {boolean} [options.disableContextMenu=true] - Prevent right-click / context menu
 * @param {boolean} [options.disableDevTools=true] - Block dev tools keyboard shortcuts (F12, Ctrl+Shift+I, etc.)
 * @param {boolean} [options.disablePrint=true] - Block printing (Ctrl+P, Cmd+P, window.print, print styles)
 * @param {boolean} [options.disableSelect=false] - Prevent text selection
 * @param {boolean} [options.disableCopy=false] - Prevent copying and cutting content
 * @param {boolean} [options.disableSave=true] - Block saving page (Ctrl+S, Cmd+S)
 * @param {boolean} [options.enableDebuggerTrap=false] - Optional interval debugger trap to impede dev tools
 */
const usePageProtection = ({
  disableContextMenu = true,
  disableDevTools = true,
  disablePrint = true,
  disableSelect = false,
  disableCopy = false,
  disableSave = true,
  enableDebuggerTrap = false,
} = {}) => {
  useEffect(() => {
    // 1. Context Menu Protection
    const handleContextMenu = (e) => {
      if (disableContextMenu) {
        e.preventDefault();
      }
    };

    if (disableContextMenu) {
      document.addEventListener('contextmenu', handleContextMenu);
    }

    // 2. Keyboard Shortcuts Protection (DevTools, Print, Save)
    const handleKeyDown = (e) => {
      const key = e.key ? e.key.toLowerCase() : '';
      const isCtrlOrMeta = e.ctrlKey || e.metaKey;

      // DevTools shortcuts
      if (disableDevTools) {
        // F12
        if (e.key === 'F12' || e.keyCode === 123) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Ctrl+Shift+I / J / C / K or Cmd+Option+I / J / C / K
        if (
          (isCtrlOrMeta && e.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) ||
          (e.metaKey && e.altKey && ['i', 'j', 'c', 'k'].includes(key))
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Ctrl+U / Cmd+Option+U (View Source)
        if (
          (isCtrlOrMeta && key === 'u') ||
          (e.metaKey && e.altKey && key === 'u')
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // Print shortcut (Ctrl+P / Cmd+P)
      if (disablePrint && isCtrlOrMeta && key === 'p') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Save shortcut (Ctrl+S / Cmd+S)
      if (disableSave && isCtrlOrMeta && key === 's') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    // 3. Print Protection (window.print, beforeprint, and CSS print media)
    let originalPrint = null;
    let printStyleElement = null;

    if (disablePrint) {
      originalPrint = window.print;
      window.print = () => {
        // Suppress programmatic printing
      };

      const handleBeforePrint = (e) => {
        if (e && e.preventDefault) {
          e.preventDefault();
        }
      };
      window.addEventListener('beforeprint', handleBeforePrint);

      // Hide content in print stylesheet
      const styleId = 'page-protection-no-print';
      if (!document.getElementById(styleId)) {
        printStyleElement = document.createElement('style');
        printStyleElement.id = styleId;
        printStyleElement.innerHTML = `
          @media print {
            html, body {
              display: none !important;
              visibility: hidden !important;
            }
          }
        `;
        document.head.appendChild(printStyleElement);
      }
    }

    // 4. Selection Protection
    const handleSelectStart = (e) => {
      if (disableSelect) {
        e.preventDefault();
      }
    };

    if (disableSelect) {
      document.addEventListener('selectstart', handleSelectStart);
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    // 5. Copy / Cut Protection
    const handleCopyCut = (e) => {
      if (disableCopy) {
        e.preventDefault();
      }
    };

    if (disableCopy) {
      document.addEventListener('copy', handleCopyCut);
      document.addEventListener('cut', handleCopyCut);
    }

    // 6. Optional Debugger Trap
    let debuggerInterval = null;
    if (enableDebuggerTrap) {
      debuggerInterval = setInterval(() => {
        const startTime = performance.now();
        // eslint-disable-next-line no-debugger
        debugger;
        const endTime = performance.now();
        if (endTime - startTime > 100) {
          // DevTools was likely open and hit the breakpoint
        }
      }, 1000);
    }

    // Cleanup on unmount or options change
    return () => {
      if (disableContextMenu) {
        document.removeEventListener('contextmenu', handleContextMenu);
      }

      window.removeEventListener('keydown', handleKeyDown, true);

      if (disablePrint) {
        if (originalPrint) {
          window.print = originalPrint;
        }
        window.removeEventListener('beforeprint', () => {});
        if (printStyleElement && printStyleElement.parentNode) {
          printStyleElement.parentNode.removeChild(printStyleElement);
        } else {
          const el = document.getElementById('page-protection-no-print');
          if (el && el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }
      }

      if (disableSelect) {
        document.removeEventListener('selectstart', handleSelectStart);
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
      }

      if (disableCopy) {
        document.removeEventListener('copy', handleCopyCut);
        document.removeEventListener('cut', handleCopyCut);
      }

      if (debuggerInterval) {
        clearInterval(debuggerInterval);
      }
    };
  }, [
    disableContextMenu,
    disableDevTools,
    disablePrint,
    disableSelect,
    disableCopy,
    disableSave,
    enableDebuggerTrap,
  ]);
};

export default usePageProtection;